// Module 4.1b · Pass C — Kill Sheet Printable IWCF Layout.
//
// Field-readable single-page layout. Designed to print cleanly on plain
// paper and survive black-and-white photocopying:
//   • Pure black text on white background — no colour-only signals.
//   • Heavy 2px / 1.5px borders for boundary clarity.
//   • Large monospace headline numbers (≥ 18pt printable equivalent).
//   • Bold + uppercase labels.
//   • Phase column carries the WORD ("KILL", "CIRCULATE") plus a thick
//     left border — never colour alone.
//   • Traceability footer with revision, all engine versions, prepared-by,
//     approved-by (Pass E will populate), generated + printed timestamps.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { useStackMode } from "@/lib/stackModeContext";
import { getKillSheet, diffKillSheetFromParent } from "@/lib/killsheet/storage";
import { computeKillSheetOutputs } from "@/lib/killsheet/compute";
import { REVISION_REASON_LABELS } from "@/lib/killsheet/schema";
import { ScheduleChart } from "@/components/killsheet/ScheduleChart";
import {
  buildSurfaceLineRow, firstKillRowIndex, SURFACE_LINE_ROW_LABEL,
  SURFACE_LINE_RESET_REMINDER, DRILL_STRING_ZERO_LABEL,
} from "@/lib/killsheet/surfaceLineRow";
import { isProActive } from "@/lib/featureAccess";
import { useEntitlement } from "@/lib/entitlement";
import UpgradeScreen from "@/components/UpgradeScreen";

const _fmt = (v, digits = 2) => {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });
};
const _fmtInt = (v) => {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString();
};
const _fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

export default function KillSheetOpPrint() {
  const navigate = useNavigate();
  const { stackMode } = useStackMode();
  const { wellId, ksId } = useParams();
  const well = useMemo(() => getWellProfile(wellId), [wellId]);
  const rec  = useMemo(() => getKillSheet(ksId),     [ksId]);

  // Live recompute on print — guarantees the printed schedule reflects the
  // exact current state of the record, with fresh provenance.computedAt.
  const outputs = useMemo(() => rec ? computeKillSheetOutputs(rec) : null, [rec]);

  // Surface-line displacement row for the printed schedule — same shared
  // builder the on-screen table uses, so paper and screen cannot diverge.
  const surfaceLineRow = useMemo(
    () => rec ? buildSurfaceLineRow({
      rigId: rec.contextSnapshot?.sessionContextIds?.rigId || "",
      pumpOutputBblStk: rec.inputs?.pumpOutputBblStk,
      pumpRateSpm: rec.inputs?.pumpRateSpm,
    }) : null,
    [rec],
  );

  // Pass E — "What Changed" diff vs parent revision.
  const diff = useMemo(() => {
    if (!rec?.parentId) return { hasParent: false, changes: [] };
    try { return diffKillSheetFromParent(rec.id); }
    catch { return { hasParent: false, changes: [] }; }
  }, [rec]);

  // Stamp the "printed at" timestamp once when the page mounts so the
  // print preview header matches the paper output.
  const [printedAt] = useState(() => new Date().toISOString());

  // Toggle a body class so the @media-print rules below can hide the app
  // header + bottom navigation chrome (which sits OUTSIDE this component
  // via the shared <Layout/> route element). The class is removed on
  // unmount so other pages keep their chrome.
  useEffect(() => {
    document.body.classList.add("ks-print-mode");
    return () => document.body.classList.remove("ks-print-mode");
  }, []);

  // A saved Kill Sheet's printed output is Pro-only, same as the live
  // outputs in KillSheetOpEditor.jsx — checked here too since a record can
  // outlive an entitlement (e.g. a lapsed subscription), and this page
  // reads the persisted record directly rather than an in-flight session.
  // Subscribed (not the module cache) so a print opened by direct URL isn't
  // gated by an entitlement fetch that is still in flight.
  const entitlement = useEntitlement();
  // Wait for the entitlement load before judging: isProActive() returns
  // false while `loading` is true, which otherwise flashes the paywall at
  // Owner/Pro users on first paint. Mirrors RequireProAccess.jsx.
  if (entitlement.loading) return null;
  if (!isProActive(entitlement)) {
    return <UpgradeScreen context="Kill Sheet printing & export" />;
  }

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to print this kill sheet, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }
  if (!rec) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Kill sheet not found.{" "}
        <Link to={`/wells/${wellId}/kill-sheets`} className="text-amber-400">Back to Kill Sheets</Link>.
      </div>
    );
  }

  return (
    <>
      {/* Print-specific CSS scoped to this view — overrides the dark theme
          for printable output. Keeps the on-screen preview legible while
          rendering pure-monochrome on paper. */}
      <style>{`
        @media screen {
          .ks-print-page {
            background: #ffffff;
            color: #000000;
            padding: 24px;
            min-height: 100vh;
          }
        }
        @media print {
          body { background: #ffffff !important; }
          .ks-print-controls { display: none !important; }
          /* Hide the shared <Layout/> chrome — top header + bottom nav —
             when the print page is mounted. The :has() guard skips the
             document's own header which lives INSIDE .ks-print-page. */
          body.ks-print-mode header:not(:has(.bx)):not(.bx),
          body.ks-print-mode nav,
          body.ks-print-mode [data-app-chrome],
          body.ks-print-mode .app-bottom-nav,
          body.ks-print-mode .app-header {
            display: none !important;
          }
          /* Belt-and-braces: hide every direct child of <body> that is NOT
             the React root + the print page. */
          body.ks-print-mode > *:not(#root):not(script):not(style):not(.ks-print-page) {
            display: none !important;
          }
          .ks-print-page {
            background: #ffffff !important;
            color: #000000 !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          @page { margin: 12mm; }
        }
        .ks-print-page, .ks-print-page * {
          color: #000000;
          background: transparent;
          font-family: "IBM Plex Sans", Arial, sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .ks-print-page .mono { font-family: "JetBrains Mono", "Courier New", monospace; }
        .ks-print-page .bx { border: 2px solid #000; }
        .ks-print-page .bx-thin { border: 1px solid #000; }
        .ks-print-page table { border-collapse: collapse; width: 100%; }
        .ks-print-page th, .ks-print-page td {
          border: 1px solid #000; padding: 4px 6px;
        }
        .ks-print-page th {
          background: #ffffff; text-transform: uppercase; font-size: 9px;
          letter-spacing: 0.12em; font-weight: 700;
        }
        .ks-print-page .label {
          font-size: 8.5px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.14em;
        }
        .ks-print-page .val {
          font-size: 14px; font-weight: 700;
        }
        .ks-print-page .val-lg {
          font-size: 22px; font-weight: 700; line-height: 1.1;
        }
      `}</style>

      {/* Controls — hidden on print */}
      <div className="ks-print-controls flex items-center justify-between gap-3 mb-4 px-4 sm:px-6 pt-4"
           data-testid="ks-print-controls">
        <button type="button"
                onClick={() => navigate(`/wells/${wellId}/kill-sheets/${ksId}`)}
                data-testid="ks-print-back"
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to editor
        </button>
        <button type="button"
                onClick={() => window.print()}
                data-testid="ks-print-print"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
                style={{ background: "var(--rs-amber, #f5b400)", color: "#0A0B0E" }}>
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
      </div>

      <article className="ks-print-page" data-testid="ks-print-page">

        {/* ===== Header ===== */}
        <header className="bx" style={{ padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
            <div>
              <div className="label">RigSentinel · IWCF Kill Sheet</div>
              <div className="val-lg mono" data-testid="ks-print-name">
                {rec.name || "(unnamed kill sheet)"}
              </div>
              <div className="mono" style={{ fontSize: 11, marginTop: 4 }}>
                Well: <strong>{well.wellName || "—"}</strong>
                {" · "}Operator: <strong>{well.operator || "—"}</strong>
                {" · "}Rig: <strong>{[well.rigName, well.rigContractor].filter(Boolean).join(" · ") || "—"}</strong>
              </div>
            </div>
            <table className="mono" style={{ width: 240, fontSize: 10 }}>
              <tbody>
                <tr><td className="label" style={{ width: 110 }}>Sheet #</td>
                    <td data-testid="ks-print-sheet-num"><strong>{rec.sheetNumber || "—"}</strong></td></tr>
                <tr><td className="label">Revision</td>
                    <td data-testid="ks-print-revision"><strong>{rec.revision || "—"}</strong></td></tr>
                <tr><td className="label">Status</td>
                    <td data-testid="ks-print-status"><strong>{(rec.status || "—").toUpperCase()}</strong></td></tr>
                <tr><td className="label">Std</td>
                    <td><strong>{rec.basis?.wellControlStandard || "—"}</strong></td></tr>
              </tbody>
            </table>
          </div>
        </header>

        {/* ===== "What Changed" diff strip (Pass E) ===== */}
        {diff.hasParent && (
          <section
            data-testid="ks-print-whatchanged"
            className="bx"
            style={{ padding: "8px 10px", marginBottom: 10 }}>
            <div className="label" style={{ marginBottom: 4 }}>
              What Changed · Rev {rec.revision || "?"} from Parent Rev {diff.parentRecord?.revision || "(unlabelled)"}
              {rec.revisionReason && (
                <span style={{ marginLeft: 8, fontWeight: 400, textTransform: "none" }}>
                  · Reason: <strong>{REVISION_REASON_LABELS[rec.revisionReason] || rec.revisionReason}</strong>
                </span>
              )}
            </div>
            {diff.changes.length === 0 ? (
              <div style={{ fontSize: 10, fontStyle: "italic" }}>
                No engineering values differ from the parent revision.
              </div>
            ) : (
              <table className="mono" style={{ fontSize: 10 }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}>Field</th>
                    <th style={{ width: "30%" }}>Parent</th>
                    <th>Current</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.changes.map((c) => (
                    <tr key={c.field} data-testid={`ks-print-whatchanged-row-${c.field}`}>
                      <td>{c.label}</td>
                      <td style={{ textDecoration: "line-through" }}>
                        {c.parent === "" || c.parent == null
                          ? "—"
                          : (Number.isFinite(Number(c.parent)) ? Number(c.parent).toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(c.parent))}
                      </td>
                      <td><strong>
                        {c.child === "" || c.child == null
                          ? "—"
                          : (Number.isFinite(Number(c.child)) ? Number(c.child).toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(c.child))}
                      </strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {rec.revisionNotes && (
              <div style={{ marginTop: 6, fontSize: 9.5, fontStyle: "italic" }}>
                Revision notes: {rec.revisionNotes}
              </div>
            )}
          </section>
        )}

        {/* ===== Engineering Basis + Frozen Inputs ===== */}
        <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div className="bx" style={{ padding: "8px 10px" }}>
            <div className="label" style={{ marginBottom: 6 }}>Engineering Basis</div>
            <table className="mono" style={{ fontSize: 11 }}>
              <tbody>
                <tr><td className="label" style={{ width: 110 }}>Kill Method</td>
                    <td data-testid="ks-print-killMethod"><strong>{rec.basis?.killMethod || "—"}</strong></td></tr>
                <tr><td className="label">Fluid Basis</td>
                    <td><strong>{rec.basis?.fluidBasis || "—"}</strong></td></tr>
                <tr><td className="label">Influx Type</td>
                    <td><strong>{rec.basis?.influxType || "—"}</strong></td></tr>
                <tr><td className="label">WC Standard</td>
                    <td><strong>{rec.basis?.wellControlStandard || "—"}</strong></td></tr>
              </tbody>
            </table>
          </div>

          <div className="bx" style={{ padding: "8px 10px" }}>
            <div className="label" style={{ marginBottom: 6 }}>Frozen Inputs</div>
            <table className="mono" style={{ fontSize: 10 }}>
              <tbody>
                <tr><td className="label" style={{ width: 130 }}>MW in (ppg)</td><td><strong>{_fmt(rec.inputs?.mwInPpg, 2)}</strong></td>
                    <td className="label" style={{ width: 130 }}>SIDPP (psi)</td><td><strong>{_fmtInt(rec.inputs?.sidppPsi)}</strong></td></tr>
                <tr><td className="label">Slow-pump (psi)</td><td><strong>{_fmtInt(rec.inputs?.slowPumpPressurePsi)}</strong></td>
                    <td className="label">SICP (psi)</td><td><strong>{_fmtInt(rec.inputs?.sicpPsi)}</strong></td></tr>
                <tr><td className="label">SPM</td><td><strong>{_fmtInt(rec.inputs?.pumpRateSpm)}</strong></td>
                    <td className="label">Pump out (bbl/stk)</td><td><strong>{_fmt(rec.inputs?.pumpOutputBblStk, 4)}</strong></td></tr>
                <tr><td className="label">Current MD (ft)</td><td><strong>{_fmtInt(rec.inputs?.currentMdFt)}</strong></td>
                    <td className="label">Current TVD (ft)</td><td><strong>{_fmtInt(rec.inputs?.currentTvdFt)}</strong></td></tr>
                <tr><td className="label">Hole size (in)</td><td><strong>{_fmt(rec.inputs?.holeSize, 3)}</strong></td>
                    <td className="label">Shoe TVD (ft)</td><td><strong>{_fmtInt(rec.inputs?.casingShoeTvdFt)}</strong></td></tr>
                <tr><td className="label">String vol (bbl)</td><td><strong>{_fmt(rec.inputs?.stringInternalVolBbl, 2)}</strong></td>
                    <td className="label">Safety margin (ppg)</td><td><strong>{_fmt(rec.inputs?.safetyMarginPpg, 2)}</strong></td></tr>
                <tr><td className="label">Bit→Shoe (bbl)</td><td><strong>{_fmt(rec.inputs?.bitToShoeVolBbl, 2)}</strong></td>
                    <td className="label">Shoe→Surface (bbl)</td><td><strong>{_fmt(rec.inputs?.shoeToSurfaceVolBbl, 2)}</strong></td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* ===== Headline outputs — KMW / ICP / FCP boxed and BIG ===== */}
        <section className="bx" style={{ padding: 8, marginBottom: 10 }}>
          <div className="label" style={{ marginBottom: 6 }}>Headline Outputs</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            <HeadlineCell label="KMW (ppg)" value={_fmt(outputs?.kmwPpg, 2)} testId="ks-print-kmw" />
            <HeadlineCell label="Initial Circulating Pressure (ICP), psi" value={_fmtInt(outputs?.icpPsi)} testId="ks-print-icp" />
            <HeadlineCell label="Final Circulating Pressure (FCP), psi" value={_fmtInt(outputs?.fcpPsi)} testId="ks-print-fcp" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 8 }}>
            <SubCell label="Volume Drill String Internal (bbl)" value={_fmt(outputs?.volStringInternalBbl, 2)} testId="ks-print-volSB" />
            <SubCell label="Strokes Surf→Bit"     value={_fmtInt(outputs?.strokesSurfaceToBit)} />
            <SubCell label="Time Surf→Bit (min)"  value={_fmt(outputs?.timeSurfaceToBitMin, 1)} />
            <SubCell label="Volume Annulus Bit→Shoe (bbl)" value={_fmt(outputs?.volBitToShoeBbl, 2)} testId="ks-print-volBS" />
            <SubCell label="Strokes Bit→Shoe"     value={_fmtInt(outputs?.strokesBitToShoe)} />
            <SubCell label="Time Bit→Shoe (min)"  value={_fmt(outputs?.timeBitToShoeMin, 1)} />
            <SubCell label="Volume Annulus Shoe→Rig Floor (bbl)" value={_fmt(outputs?.volShoeToSurfaceBbl, 2)} testId="ks-print-volSS" />
            <SubCell label="Strokes Shoe→Surf"    value={_fmtInt(outputs?.strokesShoeToSurface)} />
            <SubCell label="Time Shoe→Surf (min)" value={_fmt(outputs?.timeShoeToSurfaceMin, 1)} />
          </div>
        </section>

        {/* ===== MAASP / Pressure Limits — Pass D ===== */}
        <MaaspPrintBlock outputs={outputs} />

        {/* ===== Driller's Schedule — the headline print page asset ===== */}
        <section style={{ marginBottom: 10 }}>
          <div className="label" style={{ marginBottom: 4 }}>
            Driller&apos;s Schedule
            <span style={{ marginLeft: 8, fontWeight: 400, textTransform: "none" }}>
              ({outputs?.schedule?.method || "—"} · every {outputs?.schedule?.intervalStrokes ?? "—"} strokes)
            </span>
          </div>
          {outputs?.schedule?.ok ? (
            <table className="mono bx" data-testid="ks-print-schedule">
              <thead>
                <tr>
                  <th style={{ width: "20%" }}>Stroke</th>
                  <th style={{ width: "25%", textAlign: "right" }}>Standpipe Pressure (psi)</th>
                  <th style={{ width: "15%", textAlign: "right" }}>Time (min)</th>
                  <th style={{ width: "12%" }}>Phase</th>
                  <th>ICP / FCP / Milestone</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const schedRows = outputs.schedule.rows;
                  // Surface → Bit → Shoe → Surface milestones — same
                  // last-row-of-phase detection as the on-screen editor
                  // (ScheduleTable in KillSheetOpEditor.jsx); no calculation
                  // touched, purely which label prints on which row.
                  const lastKillIdx          = schedRows.reduce((acc, r, idx) => (r.phase === "kill" ? idx : acc), -1);
                  const lastBitToShoeIdx     = schedRows.reduce((acc, r, idx) => (r.phase === "bit-to-shoe" ? idx : acc), -1);
                  const lastShoeToSurfaceIdx = schedRows.reduce((acc, r, idx) => (r.phase === "shoe-to-surface" ? idx : acc), -1);
                  const firstKillIdx         = firstKillRowIndex(schedRows);
                  return schedRows.map((r, i) => {
                    // Phase indicator never relies on colour alone for B&W
                    // photocopy survival: WORD + thick left border.
                    const leftRule = r.phase === "circulate" ? "5px double #000" : "5px solid #000";
                    const isIcpRow     = i === 0;
                    const isFcpRow     = i === lastKillIdx && lastKillIdx > 0;
                    const isShoeRow    = lastBitToShoeIdx !== -1 && i === lastBitToShoeIdx;
                    const isSurfaceRow = lastShoeToSurfaceIdx !== -1 && i === lastShoeToSurfaceIdx;
                    const pressureTxt = r.pressurePsi?.toLocaleString() ?? "—";
                    // Surface-line displacement row — printed immediately
                    // before the method's stroke-zero kill-mud row, after
                    // any preceding circulation rows.
                    const isFirstKillRow = i === firstKillIdx;
                    return (
                      <React.Fragment key={i}>
                      {isFirstKillRow && surfaceLineRow && (
                        <tr data-testid="ks-print-schedule-surface-line-row">
                          <td className="val">
                            {surfaceLineRow.strokes == null ? "—" : surfaceLineRow.strokes.toFixed(2)}
                            <div style={{ fontSize: 8.5, fontWeight: 400 }}>
                              strokes required
                              {surfaceLineRow.wholeStrokes != null
                                ? ` · whole stroke ${surfaceLineRow.wholeStrokes.toLocaleString()}`
                                : ""}
                            </div>
                          </td>
                          <td className="val-lg" style={{ textAlign: "right" }}>
                            —
                            <div style={{ fontSize: 8.5, fontWeight: 400 }}>no scheduled pressure target</div>
                          </td>
                          <td className="val" style={{ textAlign: "right" }}>
                            {surfaceLineRow.timeMin == null ? "—" : surfaceLineRow.timeMin.toFixed(1)}
                          </td>
                          <td style={{ borderLeft: "5px dotted #000", fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>
                            surface line
                          </td>
                          <td style={{ fontSize: 10 }}>
                            <strong data-testid="ks-print-schedule-surface-label">
                              {SURFACE_LINE_ROW_LABEL}
                            </strong>
                            <div data-testid="ks-print-schedule-surface-volume" style={{ marginTop: 2 }}>
                              Surface-line volume:{" "}
                              <strong>
                                {surfaceLineRow.volumeValue == null
                                  ? "—"
                                  : `${Number(surfaceLineRow.volumeValue).toFixed(2)} ${surfaceLineRow.volumeUnit}`}
                              </strong>{" "}
                              ({surfaceLineRow.sourceNote})
                            </div>
                            {!surfaceLineRow.ok && (
                              <div data-testid="ks-print-schedule-surface-message" style={{ marginTop: 2 }}>
                                {surfaceLineRow.message}
                              </div>
                            )}
                            <div data-testid="ks-print-schedule-reset-reminder"
                                 style={{ marginTop: 3, color: "#e02020", fontWeight: 700,
                                          textTransform: "uppercase", fontSize: 10,
                                          border: "1.5px solid #e02020", padding: "2px 4px" }}>
                              {SURFACE_LINE_RESET_REMINDER}
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr data-testid={`ks-print-schedule-row-${i}`}>
                        <td className="val">{r.stroke?.toLocaleString() ?? "—"}</td>
                        <td className="val-lg" style={{ textAlign: "right" }}>
                          {pressureTxt}
                        </td>
                        <td className="val" style={{ textAlign: "right" }}>
                          {r.timeMin == null ? "—" : r.timeMin.toFixed(1)}
                        </td>
                        <td style={{ borderLeft: leftRule, fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>
                          {r.phase}
                        </td>
                        <td style={{ fontSize: 10 }}
                            data-testid={isIcpRow ? "ks-print-schedule-icp-label" : isFcpRow ? "ks-print-schedule-fcp-label" : undefined}>
                          {isIcpRow && (
                            <strong style={{ border: "1.5px solid #000", padding: "1px 4px", fontSize: 10.5, display: "inline-block" }}>
                              Initial Circulating Pressure (ICP) · {pressureTxt} psi
                            </strong>
                          )}
                          {isFcpRow && (
                            <>
                              <strong style={{ border: "1.5px solid #000", padding: "1px 4px", fontSize: 10.5, display: "inline-block" }}>
                                Final Circulating Pressure (FCP) · {pressureTxt} psi
                              </strong>
                              <div style={{ fontSize: 9, marginTop: 2 }}>Kill mud reaches bit</div>
                            </>
                          )}
                          {isShoeRow && (
                            <strong style={{ fontSize: 10 }}>Kill mud reaches shoe</strong>
                          )}
                          {isSurfaceRow && (
                            <strong style={{ fontSize: 10 }}>Kill mud reaches surface</strong>
                          )}
                          {!isIcpRow && !isFcpRow && !isShoeRow && !isSurfaceRow && "Scheduled target"}
                          {isFirstKillRow && (
                            <div data-testid="ks-print-schedule-drillstring-zero"
                                 style={{ marginTop: 2, fontWeight: 700, fontSize: 10 }}>
                              {DRILL_STRING_ZERO_LABEL}
                            </div>
                          )}
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          ) : (
            <div className="bx" style={{ padding: 8, fontSize: 11 }}>
              <strong>Schedule unavailable.</strong> Missing inputs:
              <ul style={{ margin: "4px 0 0 18px" }}>
                {(outputs?.schedule?.notes || []).map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
          {outputs?.schedule?.ok && (
            <div className="bx" style={{ padding: 8, marginTop: 8, color: "#000" }}
                 data-testid="ks-print-chart-wrapper">
              <div className="label" style={{ marginBottom: 4 }}>Step-Down Chart</div>
              <ScheduleChart schedule={outputs.schedule}
                             maaspInitialPsi={outputs.maaspInitialPsi}
                             testIdPrefix="ks-print-chart" />
            </div>
          )}
        </section>

        {/* ===== Soft notes ===== */}
        {outputs?.notes?.length > 0 && (
          <section className="bx" style={{ padding: 8, marginBottom: 10 }}>
            <div className="label" style={{ marginBottom: 4 }}>Engineering Notes</div>
            <ul style={{ margin: "0 0 0 18px", fontSize: 11 }}>
              {outputs.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </section>
        )}

        {/* ===== Traceability footer ===== */}
        <footer className="bx" style={{ padding: "8px 10px", fontSize: 9 }}
                data-testid="ks-print-footer">
          <div className="label" style={{ marginBottom: 6 }}>Engineering Traceability</div>
          <table className="mono" style={{ fontSize: 9.5 }}>
            <tbody>
              <tr>
                <td className="label" style={{ width: 140 }}>Kill Sheet Revision</td>
                <td data-testid="ks-print-footer-revision"><strong>{rec.revision || "—"}</strong></td>
                <td className="label" style={{ width: 140 }}>Formula Version</td>
                <td data-testid="ks-print-footer-formulasVersion"><strong>{outputs?.provenance?.formulasVersion || "—"}</strong></td>
              </tr>
              <tr>
                <td className="label">Units Version</td>
                <td data-testid="ks-print-footer-unitsVersion"><strong>{outputs?.provenance?.unitsVersion || "—"}</strong></td>
                <td className="label">Schedule Version</td>
                <td data-testid="ks-print-footer-scheduleVersion"><strong>{outputs?.provenance?.scheduleVersion || "—"}</strong></td>
              </tr>
              <tr>
                <td className="label">Prepared By</td>
                <td data-testid="ks-print-footer-preparedBy"><strong>{rec.preparedBy || "—"}</strong></td>
                <td className="label">Approved By</td>
                <td data-testid="ks-print-footer-approvedBy">
                  <strong>{rec.approvedBy || "—"}</strong>
                  {rec.approvedAt && <span style={{ marginLeft: 6 }}>· {_fmtDateTime(rec.approvedAt)}</span>}
                </td>
              </tr>
              <tr>
                <td className="label">Generated</td>
                <td data-testid="ks-print-footer-generated"><strong>{_fmtDateTime(outputs?.provenance?.computedAt)}</strong></td>
                <td className="label">Printed</td>
                <td data-testid="ks-print-footer-printed"><strong>{_fmtDateTime(printedAt)}</strong></td>
              </tr>
              {rec.lockedAt && (
                <tr>
                  <td className="label">Locked</td>
                  <td colSpan={3}>
                    <strong>{rec.lockedBy || "—"}</strong> · {_fmtDateTime(rec.lockedAt)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontSize: 8.5, fontStyle: "italic", borderTop: "1px solid #000", paddingTop: 4 }}>
            RigSentinel provides deterministic calculation support. Outputs must be independently
            verified by qualified personnel before any operational use.
          </div>
        </footer>
      </article>
    </>
  );
}

function HeadlineCell({ label, value, testId }) {
  return (
    <div className="bx" style={{ padding: 8, textAlign: "center" }} data-testid={testId}>
      <div className="label">{label}</div>
      <div className="val-lg mono" style={{ marginTop: 4 }}>{value}</div>
    </div>
  );
}
function SubCell({ label, value, testId }) {
  return (
    <div className="bx-thin" style={{ padding: 6, textAlign: "center" }} data-testid={testId}>
      <div className="label">{label}</div>
      <div className="val mono" style={{ marginTop: 2 }}>{value}</div>
    </div>
  );
}

// MAASP block — printable variant.
// Renders as a thick-bordered amber-leaning panel by default, escalating to
// a heavy 3px black border with a "MAASP GUARDRAIL BREACH" header band
// when any guardrail fires. Photocopy-friendly: never relies on colour
// alone — the BREACH header uses inverted black-on-white with thick rules.
function MaaspPrintBlock({ outputs }) {
  const g = outputs?.guardrails || {};
  const breach = g.kmwExceedsMamw || g.mwInExceedsMamw || g.maaspAtKmwNegative || g.sicpExceedsMaasp;

  // Hide entirely when MAASP basis wasn't entered — the document shouldn't
  // carry an empty section.
  if (!g.available) return null;

  return (
    <section
      data-testid="ks-print-maasp"
      data-breach={breach ? "true" : "false"}
      style={{
        marginBottom: 10,
        border: breach ? "3px solid #000" : "2px solid #000",
      }}>
      {breach && (
        <div data-testid="ks-print-maasp-breach"
             style={{
               background: "#000", color: "#fff",
               padding: "6px 10px",
               fontWeight: 800, fontSize: 12,
               letterSpacing: "0.18em", textTransform: "uppercase",
               textAlign: "center",
             }}>
          ⚠ MAASP Guardrail Breach — Verify Before Proceeding
        </div>
      )}
      <div style={{ padding: "8px 10px" }}>
        <div className="label" style={{ marginBottom: 6 }}>MAASP · Pressure Limits</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          <HeadlineCell label="Max Allowable MW (ppg)"
                        value={Number.isFinite(Number(outputs?.maxAllowableMwPpg))
                          ? Number(outputs.maxAllowableMwPpg).toFixed(2) : "—"}
                        testId="ks-print-mamw" />
          <HeadlineCell label="MAASP Initial (psi)"
                        value={Number.isFinite(Number(outputs?.maaspInitialPsi))
                          ? Math.round(Number(outputs.maaspInitialPsi)).toLocaleString() : "—"}
                        testId="ks-print-maaspInit" />
          <HeadlineCell label="MAASP at KMW (psi)"
                        value={Number.isFinite(Number(outputs?.maaspAtKmwPsi))
                          ? Math.round(Number(outputs.maaspAtKmwPsi)).toLocaleString() : "—"}
                        testId="ks-print-maaspKmw" />
        </div>
        {breach && (
          <ul data-testid="ks-print-maasp-messages"
              style={{ margin: "8px 0 0 18px", fontSize: 11, fontWeight: 700 }}>
            {(g.criticalMessages || []).map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        )}
      </div>
    </section>
  );
}
